/**
 * A9 WCAG contrast. Mirrored by SQL `website_contrast_ratio`; both round the
 * ratio to two decimals so threshold decisions agree.
 */

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

export function isWebsiteHexColor(value: unknown): value is string {
  return typeof value === 'string' && HEX_COLOR_RE.test(value);
}

function channelLuminance(channel: number): number {
  const normalized = channel / 255;
  return normalized <= 0.03928 ? normalized / 12.92 : Math.pow((normalized + 0.055) / 1.055, 2.4);
}

export function relativeLuminance(hex: string): number {
  if (!isWebsiteHexColor(hex)) throw new RangeError('invalid hex color');
  const red = Number.parseInt(hex.slice(1, 3), 16);
  const green = Number.parseInt(hex.slice(3, 5), 16);
  const blue = Number.parseInt(hex.slice(5, 7), 16);
  return (
    0.2126 * channelLuminance(red) +
    0.7152 * channelLuminance(green) +
    0.0722 * channelLuminance(blue)
  );
}

/** WCAG 2.x contrast ratio rounded to two decimals (1.00–21.00). */
export function contrastRatio(hexA: string, hexB: string): number {
  const a = relativeLuminance(hexA);
  const b = relativeLuminance(hexB);
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);
  return Math.round(((lighter + 0.05) / (darker + 0.05)) * 100) / 100;
}

/**
 * `#rrggbb` → `H S% L%` (the triplet format of the storefront design tokens),
 * so platform commerce components adopt a template's validated colors.
 */
export function hexToHslTriplet(hex: string): string {
  if (!isWebsiteHexColor(hex)) throw new RangeError('invalid hex color');
  const red = Number.parseInt(hex.slice(1, 3), 16) / 255;
  const green = Number.parseInt(hex.slice(3, 5), 16) / 255;
  const blue = Number.parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const lightness = (max + min) / 2;
  const delta = max - min;

  let hue = 0;
  let saturation = 0;
  if (delta !== 0) {
    saturation = delta / (1 - Math.abs(2 * lightness - 1));
    if (max === red) hue = ((green - blue) / delta) % 6;
    else if (max === green) hue = (blue - red) / delta + 2;
    else hue = (red - green) / delta + 4;
    hue *= 60;
    if (hue < 0) hue += 360;
  }
  return `${Math.round(hue)} ${Math.round(saturation * 100)}% ${Math.round(lightness * 100)}%`;
}

/** Mixes two `#rrggbb` colors; `weight` is the share of `hexB` (0..1). */
export function mixHexColors(hexA: string, hexB: string, weight: number): string {
  if (!isWebsiteHexColor(hexA) || !isWebsiteHexColor(hexB)) {
    throw new RangeError('invalid hex color');
  }
  const share = Math.min(Math.max(weight, 0), 1);
  const channel = (offset: number): string => {
    const a = Number.parseInt(hexA.slice(offset, offset + 2), 16);
    const b = Number.parseInt(hexB.slice(offset, offset + 2), 16);
    return Math.round(a + (b - a) * share)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${channel(1)}${channel(3)}${channel(5)}`;
}
